// Portal-side field definitions for each integration provider (SPEC §3.6).
//
// These mirror the Zod schemas in @ionic/hr-automations-shared-types but
// carry only the metadata a form needs to render — labels, input type,
// secret-ness, help text. Keeping them declarative means one generic
// <ProviderForm /> component can render every provider.
//
// If a provider's schema changes, update both this file and the Zod
// schemas in shared-types. A runtime drift check lives in the API's
// upsertConfig path so bad input is caught server-side too.

import type { IntegrationProviderKey } from '@ionic/hr-automations-shared-types';

export type FieldDef = TextFieldDef | TextareaFieldDef | SelectFieldDef | CheckboxFieldDef;

/**
 * Conditional-visibility rule. When present, the field is only rendered
 * (and only submitted) if the current form value at `key` matches
 * `equals` — either a single string or one-of a set. Kept intentionally
 * simple: one field watching one other field, no nesting.
 */
export interface VisibleWhen {
  key: string;
  equals: string | string[];
}

export interface TextFieldDef {
  kind: 'text';
  key: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
  secret?: boolean;
  visibleWhen?: VisibleWhen;
}

export interface TextareaFieldDef {
  kind: 'textarea';
  key: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
  secret?: boolean;
  rows?: number;
  visibleWhen?: VisibleWhen;
}

export interface SelectFieldDef {
  kind: 'select';
  key: string;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  help?: string;
  visibleWhen?: VisibleWhen;
}

export interface CheckboxFieldDef {
  kind: 'checkbox';
  key: string;
  label: string;
  help?: string;
  visibleWhen?: VisibleWhen;
}

/**
 * Returns true when `field.visibleWhen` (if any) is satisfied by the
 * current form values. Hidden fields must NOT be submitted — the form
 * filters them out in both the render loop and the submit handler.
 */
export function isFieldVisible(field: FieldDef, values: Record<string, unknown>): boolean {
  if (!field.visibleWhen) return true;
  const actual = values[field.visibleWhen.key];
  const { equals } = field.visibleWhen;
  if (Array.isArray(equals)) return equals.includes(actual as string);
  return actual === equals;
}

export interface ProviderFieldDefs {
  title: string;
  description: string;
  nonSecret: FieldDef[];
  secret: FieldDef[];
}

export const PROVIDER_FIELDS: Record<IntegrationProviderKey, ProviderFieldDefs> = {
  bamboohr: {
    title: 'BambooHR',
    description: 'Read employee directory + profiles during workflow runs.',
    nonSecret: [
      {
        kind: 'text',
        key: 'subdomain',
        label: 'Subdomain',
        required: true,
        placeholder: 'acme',
        help: 'Your BambooHR subdomain, e.g. acme for acme.bamboohr.com.',
      },
    ],
    secret: [
      {
        kind: 'text',
        key: 'apiKey',
        label: 'API key',
        required: true,
        secret: true,
        help: 'Generated under Account → API Keys in BambooHR.',
      },
    ],
  },

  docusign: {
    title: 'DocuSign',
    description: 'Send envelopes and capture signatures for letters and termination packages.',
    nonSecret: [
      {
        kind: 'select',
        key: 'environment',
        label: 'Environment',
        options: [
          { value: 'demo', label: 'Demo (account-d.docusign.com)' },
          { value: 'prod', label: 'Production (account.docusign.com)' },
        ],
        required: true,
      },
      { kind: 'text', key: 'accountId', label: 'Account ID', required: true },
      { kind: 'text', key: 'integratorKey', label: 'Integrator key', required: true },
      {
        kind: 'text',
        key: 'impersonatedUserId',
        label: 'Impersonated user ID',
        required: true,
        help: 'DocuSign user id the platform will sign as (pre-authorised via admin consent).',
      },
    ],
    secret: [
      {
        kind: 'textarea',
        key: 'rsaPrivateKey',
        label: 'RSA private key (PEM)',
        required: true,
        secret: true,
        rows: 10,
        help: 'PKCS#8 PEM. Used to sign the JWT assertion for DocuSign JWT Grant.',
      },
    ],
  },

  graph: {
    title: 'Microsoft Graph / OneDrive',
    description:
      'Optional HR-facing mirror for workflow documents. Canonical storage is S3 (§3.7).',
    nonSecret: [
      { kind: 'text', key: 'tenantId', label: 'Tenant ID (UUID)', required: true },
      { kind: 'text', key: 'clientId', label: 'Client ID (UUID)', required: true },
      {
        kind: 'text',
        key: 'oneDriveRootPath',
        label: 'OneDrive root path',
        placeholder: '/',
        help: 'Path under which workflow document folders are created.',
      },
      {
        kind: 'select',
        key: 'authMode',
        label: 'Auth mode',
        options: [
          { value: 'clientSecret', label: 'Client secret' },
          { value: 'certificate', label: 'Certificate' },
        ],
        required: true,
      },
      {
        kind: 'checkbox',
        key: 'mirrorEnabled',
        label: 'Mirror workflow documents to OneDrive',
        help: 'When off, the integration is retained but workflows skip the OneDrive copy step.',
      },
    ],
    secret: [
      {
        kind: 'text',
        key: 'clientSecret',
        label: 'Client secret (when authMode = clientSecret)',
        secret: true,
      },
      {
        kind: 'textarea',
        key: 'clientCertificatePem',
        label: 'Certificate PEM (when authMode = certificate)',
        secret: true,
        rows: 6,
      },
      {
        kind: 'textarea',
        key: 'clientCertificatePrivateKeyPem',
        label: 'Certificate private key PEM (when authMode = certificate)',
        secret: true,
        rows: 6,
      },
    ],
  },

  jira: {
    title: 'Jira',
    description:
      'Read new-hire tickets to drive Offer Letter generation. Field mapping maps Jira custom fields to candidate attributes.',
    nonSecret: [
      {
        kind: 'text',
        key: 'baseUrl',
        label: 'Base URL',
        required: true,
        placeholder: 'https://acme.atlassian.net',
      },
      { kind: 'text', key: 'userEmail', label: 'Bot user email', required: true },
    ],
    secret: [{ kind: 'text', key: 'apiToken', label: 'API token', required: true, secret: true }],
  },

  // Email supports two backends: a custom SMTP relay, or SendGrid. We
  // model the choice with a synthetic `backend` select field at the top
  // of the form — the SMTP fields use visibleWhen to only render when
  // backend === 'smtp', and the SendGrid-only apiKey secret uses
  // visibleWhen to only render when backend === 'sendgrid'. On submit
  // IntegrationDetailPage maps `backend` into the stored non-secret
  // shape: 'sendgrid' writes `{ host: 'sendgrid', fromAddress, ... }`;
  // 'smtp' writes the traditional `{ host, port, username, fromAddress,
  // startTls }`. `backend` itself is never persisted — it's a UI-only
  // discriminator that we derive on load from `config.host === 'sendgrid'`.
  //
  // Chose the `backend` select + visibleWhen pattern (over a single
  // `host` select with a "real host" companion) because it keeps each
  // field's meaning stable and renders the form's two modes without
  // embedding sentinels in user-facing inputs.
  email: {
    title: 'Email',
    description:
      'Route this company\u2019s outbound email through SendGrid or a custom SMTP server. Leave unconfigured to use the platform SES path (ADR 0003).',
    nonSecret: [
      {
        kind: 'select',
        key: 'backend',
        label: 'Backend',
        required: true,
        options: [
          { value: 'smtp', label: 'SMTP (custom host)' },
          { value: 'sendgrid', label: 'SendGrid' },
        ],
        help: 'SendGrid uses the platform API key unless you provide a company-specific override below.',
      },
      { kind: 'text', key: 'fromAddress', label: 'From address', required: true },
      {
        kind: 'text',
        key: 'host',
        label: 'SMTP host',
        required: true,
        visibleWhen: { key: 'backend', equals: 'smtp' },
      },
      {
        kind: 'text',
        key: 'port',
        label: 'SMTP port',
        required: true,
        placeholder: '587',
        visibleWhen: { key: 'backend', equals: 'smtp' },
      },
      {
        kind: 'text',
        key: 'username',
        label: 'Username',
        required: true,
        visibleWhen: { key: 'backend', equals: 'smtp' },
      },
      {
        kind: 'checkbox',
        key: 'startTls',
        label: 'Use STARTTLS',
        visibleWhen: { key: 'backend', equals: 'smtp' },
      },
    ],
    secret: [
      {
        kind: 'text',
        key: 'password',
        label: 'Password',
        required: true,
        secret: true,
        visibleWhen: { key: 'backend', equals: 'smtp' },
      },
      {
        kind: 'text',
        key: 'apiKey',
        label: 'SendGrid API key',
        secret: true,
        help: 'Leave blank to use the platform SendGrid key.',
        visibleWhen: { key: 'backend', equals: 'sendgrid' },
      },
    ],
  },
};
